<template>
    <div>
      <v-dialog
        v-model="show"
        max-width="60vh"
        scrollable
      >
        <v-card>
          <v-card-title>
            L&D Approvers
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="show = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="py-3">
            <v-data-table
              :headers="tableHeaders"
              :items="approvers"
              :items-per-page="10"
            >
              <template v-slot:[`item.Employee_Full_Name`]="{ item, index }">
                <v-select
                  v-if="editMode && currentRowIndex === index"
                  label="Select employee"
                  v-model="currentItem.Employee_ID"
                  :items="formattedEmployees"
                  item-text="full_name"
                  item-value="employee_id"
                ></v-select>
                <span v-else>{{ item.Employee_Full_Name }} </span>
              </template>
              <template v-slot:[`item.Actions`]="{item, index}">
                <v-btn
                  :disabled="editMode && (currentRowIndex !== index) || (currentRowIndex === index && canUpdate)"
                  :color="editMode && currentRowIndex === index ? 'success' : 'primary'"
                  small
                  icon
                  @click="onEditClick(item, index)"
                >
                  <v-icon>
                    {{ currentRowIndex === index ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
                  </v-icon>
                </v-btn>
                <v-btn
                  small
                  icon
                  color="red darken-2"
                  v-if="index !== currentRowIndex"
                  :disabled="editMode"
                  @click="onDeleteClick(item)"
                >
                  <v-icon>mdi-delete-circle</v-icon>
                </v-btn>
                <v-btn
                  v-if="editMode && currentRowIndex === index"
                  color="primary"
                  small
                  icon
                  @click="onCancelClick"
                >
                  <v-icon>mdi-close-circle</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              small
              :disabled="editMode"
              @click="createNewApproverDialog = !createNewApproverDialog"
            >
              Add New
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        max-width="500"
        v-model="createNewApproverDialog"
      >
        <v-card>
          <v-card-title>
            New Approvers
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="createNewApproverDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-4">
            <v-select
              label="Select employee"
              v-model="employee_ID"
              :items="formattedEmployees"
              item-text="full_name"
              item-value="employee_id"
            ></v-select>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              small
              color="primary"
              :disabled="canSave"
              @click="createNewApprover"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <ConfirmOrDeniedDialog
        v-if="deleteApproverDialog"
        v-model="deleteApproverDialog"
        :mode="confirmOrDeniedDialogModes.delete"
        @onDeleteClick="deleteApprover"
        @onCancelClick="cancelDelete"
      />
    </div>
  </template>
<script>
export default {
  name: 'ApproversManager',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentRowIndex: null,
      currentItem: null,
      employee_ID: null,
      createNewApproverDialog: false,
      approvers: [],
      employees: [],
      deleteApproverDialog: false,
      editMode: false,
      tableHeaders: [
        {
          text: 'User',
          value: 'Employee_Full_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Actions',
          value: 'Actions',
          align: 'center',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          width: 100,
          sortable: false
        }
      ]
    }
  },
  mounted () {
    this.getApprovers()
    this.getListOfEmployee()
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    canUpdate () {
      return !this.formattedEmployees.some(emp => emp.employee_id === this.currentItem.Employee_ID)
    },
    canSave () {
      return !this.employee_ID
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    formattedEmployees () {
      const approverIds = new Set(this.approvers.map(approver => approver.Employee_ID))

      return this.employees
        .filter(employee => !approverIds.has(employee.employee_id))
        .map(emp => ({
          full_name: emp.full_name,
          employee_id: emp.employee_id
        }))
    }

  },
  methods: {
    onEditClick (item, index) {
      if (this.editMode) {
        this.updateApprover()
      } else {
        this.tableHeaders[0].sortable = false
        this.currentItem = Object.assign({}, item)
        this.currentRowIndex = index
      }
      this.editMode = !this.editMode
    },
    onCancelClick () {
      this.editMode = false
      this.currentItem = null
      this.currentRowIndex = null
      this.tableHeaders[0].sortable = true
    },
    onDeleteClick (item) {
      this.deleteApproverDialog = true
      this.currentItem = Object.assign({}, item)
    },
    cancelDelete () {
      this.deleteApproverDialog = false
      this.currentItem = null
    },
    getApprovers () {
      this.axios.get('/api/l_and_d_approvers', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.approvers = res.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    getListOfEmployee () {
      this.axios.get('/api/employeelist', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.employees = res.data
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    createNewApprover () {
      this.axios.post('/api/l_and_d_approver', {
        Employee_ID: this.employee_ID
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.createNewApproverDialog = false
        this.employee_ID = null
        this.getApprovers()
        this.$store.dispatch('setSuccess', { successMsg: 'New l&d approver successfully created' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    updateApprover () {
      this.axios.put(`/api/l_and_d_approver/${this.currentItem.Approver_ID}`, {
        Employee_ID: this.currentItem.Employee_ID
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.getApprovers()
        this.onCancelClick()
        this.$store.dispatch('setSuccess', { successMsg: 'l&d approver successfully updated' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    },
    deleteApprover () {
      this.axios.delete(`/api/l_and_d_approver/${this.currentItem.Approver_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(() => {
        this.cancelDelete()
        this.getApprovers()
        this.$store.dispatch('setSuccess', { successMsg: 'l&d approver successfully deleted' })
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      })
    }
  }
}
</script>
